<template>
  <div class="offer-periods-line form-row" v-if="isNotDestroy && !archived">
    <div class="col-md-5 p-1 text-left">
      <div class="row">
        <div class="col p-1">
          <label class="font-montserrat"> {{ $t("OfferPeriodRow.from") }} </label>
          <date-picker :value="startDate" :disabledToday="disabledDate" @input="onDateChange" class="p-t-10 date-picker--small" />
        </div>

        <div class="col p-1">
          <label class="font-montserrat">{{ $t("OfferPeriodRow.time_code") }}</label>
          <multiselect v-model="timeCode"
                   track-by="id"
                   label="name"
                   :placeholder="$t('OfferPeriodRow.time_code')"
                   :showLabels="false"
                   :allow-empty="true"
                   :options="timeCodesOptions"
                   data-vv-name="timeCode"
                   @input="onTimeCodeChange" class="p-t-10 multiselect--size" />
        </div>
      </div>
    </div>

    <div class="col-md-6 p-1 text-left">
      <div class="row">
        <div class="col p-1">
          <label class="font-montserrat"> {{ $t("OfferPeriodRow.at") }} </label>
          <time-picker :value="startTime"
                       @input="onStartTimeChange"
                       class="p-t-10 time-picker--small" />
        </div>

        <div class="col p-1">
          <label class="font-montserrat"> {{ $t("OfferPeriodRow.to") }} </label>
          <time-picker :value="endTime"
                       @input="onEndTimeChange"
                       class="p-t-10 time-picker--small" />
        </div>

        <div class="col p-1">
          <label class="font-montserrat">{{ $t("OfferPeriodRow.effective_work_minutes") }}</label>
          <duration-time-input
            :value="durationMinutes"
            @input="onDurationChange"
            :show-icon="true"
            class="p-t-10  time-picker--small" />
        </div>
      </div>
    </div>


    <div class="col-md-1 p-2 text-right">
      <label>&nbsp;</label>
      <div class="input-group p-t-10">
        <button v-show="offerPeriodsCount > 1" class="btn btn-icon-dark" @click="deleteConfirm">
          <i class="fa fa-trash-o"> </i>
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment'
import DatePicker from '@/components/pages/DatePicker'
import TimePicker from '@/components/pages/TimePicker'
import DurationTimeInput from '@/components/timeCodes/DurationTimeInput'
import { convertMinutesToDurationTime } from '@/helpers/timeCodes'

export default {
  name: 'OfferPeriodForm',
  props: ['timeCodesOptions', 'offerPeriod', 'index', 'offerPeriodsCount', 'disabledDate'],
  components: { DatePicker, TimePicker, DurationTimeInput },
  data: function() {
    return {
      startDate:    null,
      startTime:    null,
      endTime:      null,
      durationMinutes: 0,
      isNotDestroy: true,
      timeCode:     { id: null, name: null },
      archived: this.offerPeriod.archived
    }
  },

  beforeMount() {
    let startAt = this.offerPeriod.start_at
    startAt = startAt ? moment(startAt) : moment()
    this.startDate = startAt.format("DD/MM/YYYY")

    this.setPeriodValuesToPickers()
    this.setDefaultEndTime()
  },

  filters: {
    toTime(minutes) {
      return convertMinutesToDurationTime(minutes, false)
    }
  },

  methods: {
    /**
     * Data populators
     */
    populateDateAndTimes() {
      let startAt = this.offerPeriod.start_at
      let endAt = this.offerPeriod.end_at

      startAt = startAt ? moment(startAt) : moment()
      endAt = endAt ? moment(endAt) : moment().add(1, 'hour')

      this.setDateAndTime(startAt, endAt)
    },

    populateDurationTime() {
      this.setDurationMinutesFrom(this.offerPeriod)
    },

    populateTimeCode() {
      if (this.offerPeriod.time_code) {
        this.timeCode.id = this.offerPeriod.time_code.id
        this.timeCode.name = this.offerPeriod.time_code.name
      } else if (this.offerPeriod.time_code_id) {
        this.timeCode.id = this.offerPeriod.time_code_id
        this.timeCode.name = this.offerPeriod.time_code_name
      }
    },

    setPeriodValuesToPickers() {
      this.resetDurationMinutes()
      this.resetTimeCode();
      this.populateDateAndTimes();
      this.populateTimeCode();
      this.populateDurationTime();
    },

    setDefaultEndTime() {
      if (this.startTime) {
        let startAt = this.offerPeriod.start_at
        let endAt = this.offerPeriod.end_at

        startAt = startAt ? moment(startAt) : moment()
        endAt = endAt instanceof Object ? moment().add(1, 'hour') : moment(endAt)

        this.setDateAndTime(startAt, endAt)
      }
    },

    setDateAndTime(startAt, endAt) {
      this.startDate = startAt.format("DD/MM/YYYY")
      this.startTime = startAt.format("HH:mm")
      this.endTime = endAt.format("HH:mm")
    },

    /**
     * Event callbacks
     */
    onTimeCodeChange() {
      this.startTime = this.timeCode.start_time
      this.endTime = this.timeCode.end_time
      this.setDurationMinutesFrom(this.timeCode)
      this.updatePeriod()
    },

    onDateChange(value) {
      if (value.length != 0) { this.startDate = value }
      this.updatePeriod()
    },

    onStartTimeChange(value) {
      this.startTime = value
      this.resetTimeCode()
      this.resetDurationMinutes()
      this.updatePeriod()
    },

    onEndTimeChange(value) {
      this.endTime = value
      this.resetTimeCode()
      this.resetDurationMinutes()
      this.updatePeriod()
    },

    onDurationChange(value) {
      this.durationMinutes = value
      this.resetTimeCode()
      this.updatePeriod()
    },

    async deleteConfirm() {
      try {
        await this.$dialog.confirm(this.$i18n.t('OfferPeriodRow.delete_confirmation'))
        if (this.offerPeriod.id) { this.isNotDestroy = false }

        this.$emit('removePeriod', this.index)
      } catch (error) {}
    },

    /**
     * Event emitters
     */
    updatePeriod() {
      let { startAt, endAt } = this.buildPeriodTime()
      this.$emit('updatePeriod', this.index, {
        effective_work_minutes: this.durationMinutes,
        time_code_id:           this.timeCode.id,
        time_code_name:         this.timeCode.name,
        start_at:               startAt.format(),
        end_at:                 endAt.format(),
      })
    },

    /**
     * Helpers
     */
    setDurationMinutesFrom(object) {
      if (typeof(object) === 'undefined') return
      if (object.effective_work_minutes === 0) return

      this.durationMinutes = object.effective_work_minutes
    },

    buildPeriodTime() {
      let startAt = moment(`${this.startDate} ${this.startTime}`, "DD/MM/YYYY HH:mm")
      let endAt = moment(`${this.startDate} ${this.endTime}`,  "DD/MM/YYYY HH:mm")

      if (endAt.isSameOrBefore(startAt)) { endAt.add(1, 'days') }

      return { startAt , endAt }
    },

    resetTimeCode() {
      this.timeCode = { id: null, name: null }
    },

    resetDurationMinutes() {
      this.durationMinutes = 0
    }
  },
  watch: {
    offerPeriodsCount: function() {
      this.setPeriodValuesToPickers();
    }
  }
}
</script>

<style lang="css" scoped>
  .offer-periods-line td + td{
    padding-left: 5px !important;
  }
  .time-picker--small{
    max-width: 110px;
    min-width: 95px;
  }
  .date-picker--small{
    max-width: 150px;
    min-width: 115px;
  }
  .multiselect{
    min-height: initial !important;
  }
  .multiselect__tags{
    padding-top: 5px !important;
    min-height: initial !important;
  }
  .multiselect--size{
    min-width: 170px;
  }
  .multiselect__option {
    white-space: normal;
  }
  .min-width-200 {
    min-width: 200px;
  }
  .font-montserrat{
    font-size: 10.5px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  label {
    overflow: hidden;
    white-space: nowrap;
    margin-bottom: 0;
  }
</style>



// WEBPACK FOOTER //
// src/components/offers/OfferPeriodForm.vue